import React, { useEffect, useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, Gift, Building2, Code, MapPin, Play, Sparkles, Check, ChevronDown, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FoundingCounter from "@/components/FoundingCounter";
import FoundingDriveCountdown from "@/components/FoundingDriveCountdown";
// Below-the-fold widgets — code-split so they don't bloat the initial Landing
// chunk (each was previously parsed + executed on main thread before LCP).
const LiveActivityWidget = lazy(() => import("@/components/LiveActivityWidget"));
const NearMeFloatingWidget = lazy(() => import("@/components/NearMeFloatingWidget"));
import TrustStrip from "@/components/TrustStrip";
import VideoCard from "@/components/VideoCard";
import PrelaunchVipModal from "@/components/PrelaunchVipModal";
import PromoHero from "@/components/PromoHero";
import Seo, { orgSchema, websiteSchema, faqSchemaFromItems } from "@/components/Seo";
import { useI18n } from "@/lib/i18n";

// Pexels supports query-string resizing. Limiting to ~1600w + WebP-equivalent
// compression (tinysrgb) cuts each of these from ~3-5 MB down to ~200 KB.
// This is THE biggest single Lighthouse perf fix on the landing page.
const PEXELS_OPTS = "?auto=compress&cs=tinysrgb&w=1600&fit=crop";
const PEXELS_OPTS_SM = "?auto=compress&cs=tinysrgb&w=800&fit=crop";
const HERO_BG = `https://images.pexels.com/photos/6942352/pexels-photo-6942352.jpeg${PEXELS_OPTS}`;
const HERO_BG_SM = `https://images.pexels.com/photos/6942352/pexels-photo-6942352.jpeg${PEXELS_OPTS_SM}`;
const HOTEL_IMG = `https://images.pexels.com/photos/97083/pexels-photo-97083.jpeg${PEXELS_OPTS}`;
const BUSINESS_IMG = `https://images.pexels.com/photos/8422729/pexels-photo-8422729.jpeg${PEXELS_OPTS}`;
const DEVELOPER_IMG = `https://images.pexels.com/photos/270404/pexels-photo-270404.jpeg${PEXELS_OPTS}`;

export default function Landing() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const [videos, setVideos] = useState([]);
    const [offers, setOffers] = useState([]);
    const [apps, setApps] = useState([]);
    const [customFaq, setCustomFaq] = useState([]);
    const [vipModal, setVipModal] = useState({ open: false, source: "" });
    const openVip = (source) => () => setVipModal({ open: true, source });

    useEffect(() => {
        (async () => {
            try {
                const [v, o, a, f] = await Promise.all([
                    api.get("/videos?featured_only=false"),
                    api.get("/offers"),
                    api.get("/apps"),
                    api.get("/faq"),
                ]);
                setVideos(v.data.slice(0, 6));
                setOffers(o.data.slice(0, 6));
                setApps(a.data.slice(0, 4));
                setCustomFaq(Array.isArray(f.data) ? f.data : []);
            } catch {}
        })();
    }, []);

    return (
        <div data-testid="landing-page">
            <Seo
                title="ADN Local Rewards · Founding VIP membership for Jacksonville"
                description="Join 50,000 Founding VIPs in Jacksonville for $25 one-time and lock in $9.99/month for life. Hand-picked local offers, $100 hotel voucher, AI-certified app marketplace."
                path="/"
                schema={[
                    orgSchema,
                    websiteSchema,
                    faqSchemaFromItems(Array.from({ length: 20 }, (_, i) => [t(`faq.q${i + 1}`), t(`faq.a${i + 1}`)])),
                ]}
            />
            {/* 90-DAY FOUNDING VIP DRIVE COUNTDOWN — pinned above the hero */}
            <FoundingDriveCountdown />

            {/* OFFICIAL TRAILER — full-width, muted autoplay, tap-to-unmute */}
            <PromoHero
                src="https://customer-assets-agu9un31.emergentagent.net/job_adn-community-app/artifacts/2n2rkewb_ADN-LOCAL-PROM0-1MIN.mp4"
                title="ADN Local · Official Trailer (2026)"
            />

            {/* HERO */}
            <section className="relative overflow-hidden bg-[#0A1128] text-white" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img
                        src={HERO_BG}
                        srcSet={`${HERO_BG_SM} 800w, ${HERO_BG} 1600w`}
                        sizes="100vw"
                        alt="Jacksonville night sky fireworks"
                        width="1600"
                        height="900"
                        fetchPriority="high"
                        decoding="async"
                        className="w-full h-full object-cover opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128]/95 via-[#0A1128]/85 to-[#050814]" />
                    <div className="absolute inset-0 stars-bg opacity-60" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-[#9B1B30]/30 border border-[#D4AF37]/30 text-[#D4AF37]" data-testid="hero-eyebrow">
                            <Sparkles className="w-3.5 h-3.5" />
                            <span className="eyebrow">America 250 · Pre-Launch · ADN Local AI™</span>
                        </div>
                        <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
                            Shop Smarter.<br />
                            <span className="text-[#D4AF37]">Not Harder.</span>
                        </h1>
                        {/* The 5-verb value prop from the mailer, front and center */}
                        <div className="mt-4 flex flex-wrap gap-2 text-sm font-heading uppercase tracking-widest" data-testid="hero-verbs">
                            <span className="text-[#D4AF37]">Ask.</span>
                            <span className="text-white">Find.</span>
                            <span className="text-[#D4AF37]">Compare.</span>
                            <span className="text-white">Save.</span>
                            <span className="text-[#D4AF37]">Buy.</span>
                        </div>
                        <p className="mt-5 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed font-accent">
                            ADN-Local.com is your <span className="text-[#D4AF37] font-semibold">AI shopping assistant for local businesses</span>. Just tell our AI what you need — a sectional under $2,500, a hairstylist under $150, dinner for 4 tonight — and we&rsquo;ll find, compare, and connect you to trusted local businesses in seconds. Real businesses. Real products. Real people. <span className="text-white font-semibold">All in one place.</span>
                        </p>
                        <p className="mt-3 max-w-xl text-sm text-white/85 leading-relaxed">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-600/25 border border-emerald-400/40 text-emerald-200 text-[11px] uppercase tracking-widest font-bold mr-2">Free to use</span>
                            Search and discover local businesses in your area — free, no account required. <span className="text-[#D4AF37] font-semibold">VIP members unlock</span> the $100 hotel voucher, exclusive member-only deals, and every extra incentive.
                        </p>
                        <p className="mt-4 max-w-xl text-sm sm:text-base text-white/80 leading-relaxed">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#9B1B30]/40 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold mr-2">Member Rate</span>
                            We&rsquo;re launching alongside the <span className="text-[#D4AF37] font-semibold">250-year celebration of the United States</span>. You&rsquo;re in the <span className="text-white font-semibold">pre-launch window</span> — lock in Founding VIP <span className="text-white/70">(consumer membership)</span> for <span className="text-[#D4AF37] font-black text-lg align-baseline">$5/month for life</span>. After launch the member rate returns to <span className="text-white">$9.99/mo for life</span>.
                        </p>
                        <p className="mt-3 max-w-xl text-sm text-white/80 leading-relaxed">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#D4AF37]/25 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold mr-2">Business Rate</span>
                            Local business owner? The <span className="text-[#D4AF37] font-semibold">first 100 Jacksonville businesses</span> lock in Founding Business Partner™ status — <span className="text-white font-semibold">$0/month for life</span>. After that, standard business pricing is $49/mo.
                        </p>
                        <div className="mt-4 max-w-xl text-sm text-white/75 leading-relaxed" data-testid="hero-city-roadmap">
                            <span className="uppercase tracking-widest text-[10px] text-[#D4AF37] font-bold">City Launch Roadmap</span>
                            <br />
                            Jacksonville <span className="text-white/60">first.</span> Then over the year — <span className="text-white">Orlando · Miami · Tampa · Ft. Lauderdale / West Palm Beach.</span>
                        </div>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Button
                                onClick={openVip("landing-hero")}
                                className="h-12 px-6 bg-[#9B1B30] hover:bg-[#B8233C] text-white border-b-2 border-[#D4AF37] rounded-sm text-base"
                                data-testid="hero-join-btn"
                            >
                                Reserve $5/mo for Life <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            <Button
                                onClick={() => navigate("/offers")}
                                variant="outline"
                                className="h-12 px-6 border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 bg-transparent rounded-sm text-base"
                                data-testid="hero-ask-btn"
                            >
                                <Sparkles className="w-4 h-4 mr-2" /> Try the AI Assistant
                            </Button>
                        </div>
                        <div className="mt-8 flex items-center gap-6 text-xs text-white/85 flex-wrap">
                            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Secure &amp; privacy-first</div>
                            <div className="flex items-center gap-2"><Gift className="w-4 h-4 text-[#D4AF37]" /> $100 Hotel voucher included</div>
                            <div className="flex items-center gap-2"><Star className="w-4 h-4 text-[#D4AF37]" /> $5/mo for life · locked forever</div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <FoundingCounter />
                        <div className="mt-4 text-xs text-white/70 text-center">Pre-launch rate resets to $9.99/mo when the clock hits zero.</div>
                    </div>
                </div>
                <div className="stripe-divider" />
            </section>

            {/* LIVE ACTIVITY WIDGET — social proof, real counts + rolling ticker */}
            <Suspense fallback={null}><LiveActivityWidget /></Suspense>

            {/* Trust strip — credibility anchor above the fold */}
            <TrustStrip />

            {/* HOW IT WORKS — 5-verb value prop from the mailer */}
            <section className="bg-[#FDFBF7]" data-testid="how-verbs">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <div className="text-center mb-10">
                        <div className="eyebrow text-[#9B1B30]">How ADN Local AI works</div>
                        <h2 className="font-heading text-3xl sm:text-4xl mt-2 text-[#0A1128]">Just ask. We do the rest.</h2>
                        <p className="mt-3 text-[#1A1D24]/75 max-w-2xl mx-auto">
                            No more searching dozens of websites. Tell our AI what you need in plain language — a budget, a style, an occasion — and get instant local recommendations you can trust.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                            ["Ask", "\"I need a sectional under $2,500.\""],
                            ["Find", "AI surfaces trusted local businesses in seconds."],
                            ["Compare", "See side-by-side options — price, rating, distance."],
                            ["Save", "Member-only deals and exclusive offers."],
                            ["Buy", "Connect and purchase — all in one place."],
                        ].map(([verb, blurb], i) => (
                            <div key={verb} className="bg-white border border-black/10 rounded-sm p-5 text-center">
                                <div className="w-8 h-8 mx-auto rounded-full bg-[#9B1B30] text-white flex items-center justify-center text-sm font-bold">{i + 1}</div>
                                <div className="mt-3 font-heading text-xl text-[#0A1128]">{verb}.</div>
                                <div className="mt-2 text-xs text-[#1A1D24]/75 leading-relaxed">{blurb}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MEMBER PRICING — explicit comparison so consumers understand exactly
                what's free and what's gated behind VIP. Sits between "how verbs"
                (what the platform does) and "two audiences" (who it's for). */}
            <section className="bg-white" data-testid="free-vs-vip">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <div className="text-center mb-10">
                        <div className="eyebrow text-[#9B1B30]">Member pricing · For consumers</div>
                        <h2 className="font-heading text-3xl sm:text-4xl mt-2 text-[#0A1128]">
                            Anyone can search. VIP Members cash in.
                        </h2>
                        <p className="mt-3 max-w-2xl mx-auto text-[#1A1D24]/75">
                            <span className="font-semibold text-[#0A1128]">For consumers &amp; shoppers.</span> Search local businesses in your area free — no signup required. Founding VIP Membership unlocks every deal, voucher, and reward the platform offers.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
                        {/* FREE tier */}
                        <div className="bg-[#FDFBF7] border-2 border-black/10 rounded-sm p-6" data-testid="tier-free">
                            <div className="flex items-baseline justify-between mb-2">
                                <div className="eyebrow text-[#1A1D24]/75">Free</div>
                                <div className="font-heading text-3xl text-[#0A1128]">$0</div>
                            </div>
                            <h3 className="font-heading text-xl text-[#0A1128] mb-4">Browse local — no signup</h3>
                            <ul className="space-y-2 text-sm text-[#1A1D24]/85">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> AI-powered local business search</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Ask · Find · Compare — see local options in your area</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Browse Jacksonville business profiles &amp; menus</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Read reviews, see hours &amp; locations</li>
                                <li className="flex items-start gap-2 text-[#1A1D24]/50"><X className="w-4 h-4 mt-1 shrink-0" /> Member-only deals &amp; VIP pricing</li>
                                <li className="flex items-start gap-2 text-[#1A1D24]/50"><X className="w-4 h-4 mt-1 shrink-0" /> $100 hotel voucher</li>
                                <li className="flex items-start gap-2 text-[#1A1D24]/50"><X className="w-4 h-4 mt-1 shrink-0" /> Ambassador rewards &amp; loyalty streaks</li>
                            </ul>
                        </div>
                        {/* VIP tier — gold-accent card */}
                        <div className="bg-[#0A1128] text-white border-2 border-[#D4AF37] rounded-sm p-6 relative shadow-xl" data-testid="tier-vip">
                            <div className="absolute -top-3 left-6 px-3 py-1 rounded-sm bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-widest font-black">
                                Founding VIP · Pre-Launch
                            </div>
                            <div className="flex items-baseline justify-between mb-2 mt-2">
                                <div className="eyebrow text-[#D4AF37]">VIP</div>
                                <div className="text-right">
                                    <div className="font-heading text-3xl text-[#D4AF37]">$5<span className="text-sm text-white/60">/mo</span></div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/60">for life · locked forever</div>
                                </div>
                            </div>
                            <h3 className="font-heading text-xl mb-4">Everything unlocked</h3>
                            <ul className="space-y-2 text-sm text-white/90">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Everything in Free — no limits</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> <span className="text-[#D4AF37] font-semibold">$100 Hotel Voucher</span> — good at 1000s of properties</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> All member-only deals from local businesses</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> VIP pricing not visible to free users</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Ambassador referral rewards</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Loyalty streaks &amp; birthday offers</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Priority AI Concierge access</li>
                            </ul>
                            <Button
                                onClick={openVip("landing-tier-vip")}
                                data-testid="tier-vip-cta"
                                className="w-full mt-5 bg-[#9B1B30] hover:bg-[#B8233C] text-white border-b-2 border-[#D4AF37] rounded-sm"
                            >
                                Reserve $5/mo for Life <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            <p className="mt-2 text-[10px] text-center text-white/60">
                                No payment now — activation link goes out 12/4/2026. Everything&rsquo;s free until then.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* BUSINESS PRICING — parallel to the member pricing card above, but
                built for local business owners. Explicit tier structure removes
                the ambiguity about who pays what. */}
            <section className="bg-[#FDFBF7]" data-testid="business-pricing">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <div className="text-center mb-10">
                        <div className="eyebrow text-[#9B1B30]">Business pricing · For local owners</div>
                        <h2 className="font-heading text-3xl sm:text-4xl mt-2 text-[#0A1128]">
                            List your business. Own your city.
                        </h2>
                        <p className="mt-3 max-w-2xl mx-auto text-[#1A1D24]/75">
                            <span className="font-semibold text-[#0A1128]">For business owners &amp; local shops.</span> The first 100 Jacksonville businesses claim <span className="text-[#9B1B30] font-semibold">Founding Business Partner™</span> status — a lifetime listing at $0 forever. After 100 slots close, standard business pricing begins.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
                        {/* Founding Business Partner tier */}
                        <div className="bg-white border-2 border-[#D4AF37] rounded-sm p-6 relative shadow-xl" data-testid="tier-founding-biz">
                            <div className="absolute -top-3 left-6 px-3 py-1 rounded-sm bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-widest font-black">
                                Founding · First 100 · Pre-Launch
                            </div>
                            <div className="flex items-baseline justify-between mb-2 mt-2">
                                <div className="eyebrow text-[#9B1B30]">Founding Partner</div>
                                <div className="text-right">
                                    <div className="font-heading text-3xl text-[#9B1B30]">$0<span className="text-sm text-[#1A1D24]/60">/mo</span></div>
                                    <div className="text-[10px] uppercase tracking-widest text-[#1A1D24]/60">for life · locked forever</div>
                                </div>
                            </div>
                            <h3 className="font-heading text-xl text-[#0A1128] mb-4">Free lifetime listing</h3>
                            <ul className="space-y-2 text-sm text-[#1A1D24]/85">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Permanent public business profile page</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> <span className="text-[#9B1B30] font-semibold">One always-active offer for life</span></li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Discovered by ADN&rsquo;s AI shopping assistant</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Self-service portal (edit profile, hours, hero, offers)</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Photo gallery + Google-Maps integration</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Community-network exposure to VIP members</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> No commission on redemptions — ever</li>
                            </ul>
                            <Button onClick={() => navigate("/partners")} data-testid="tier-founding-biz-cta" className="w-full mt-5 bg-[#9B1B30] hover:bg-[#B8233C] text-white border-b-2 border-[#D4AF37] rounded-sm">
                                Claim Founding Business Slot <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            <p className="mt-2 text-[10px] text-center text-[#1A1D24]/60">
                                Requires active ADN Member sponsor. Core ADN Rule applies.
                            </p>
                        </div>
                        {/* Standard Business tier (post-100) */}
                        <div className="bg-white border-2 border-black/10 rounded-sm p-6" data-testid="tier-standard-biz">
                            <div className="flex items-baseline justify-between mb-2">
                                <div className="eyebrow text-[#1A1D24]/75">Standard Business</div>
                                <div className="text-right">
                                    <div className="font-heading text-3xl text-[#0A1128]">$49<span className="text-sm text-[#1A1D24]/60">/mo</span></div>
                                    <div className="text-[10px] uppercase tracking-widest text-[#1A1D24]/60">after founding closes</div>
                                </div>
                            </div>
                            <h3 className="font-heading text-xl text-[#0A1128] mb-4">Post-launch business tier</h3>
                            <ul className="space-y-2 text-sm text-[#1A1D24]/85">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Everything in Founding — same feature set</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> Monthly billing · cancel anytime</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" /> No commission on redemptions</li>
                                <li className="flex items-start gap-2 text-[#1A1D24]/50"><X className="w-4 h-4 mt-1 shrink-0" /> Lifetime lock (Founding-only benefit)</li>
                            </ul>
                            <div className="mt-5 rounded-sm bg-[#9B1B30]/10 border border-[#9B1B30]/30 px-3 py-3 text-xs text-[#0A1128]">
                                Only <span className="font-bold text-[#9B1B30]">the first 100 Jacksonville businesses</span> lock in $0 forever. Once claimed, all new businesses join at the standard tier.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FOR CONSUMERS / FOR BUSINESS OWNERS — the two-audience story from the mailer */}
            <section className="bg-[#0A1128] text-white" data-testid="two-audiences">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid md:grid-cols-2 gap-6">
                    <div className="bg-white/[0.04] border border-white/10 rounded-sm p-6" data-testid="for-consumers">
                        <div className="eyebrow text-[#D4AF37] mb-2">For consumers</div>
                        <h3 className="font-heading text-2xl mb-3">Tell us what you need. We&rsquo;ll help you find it.</h3>
                        <ul className="space-y-2 text-sm text-white/85">
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Save time — no more searching dozens of websites</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Compare easily — side-by-side, real options</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Discover local businesses you didn&rsquo;t know existed</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Get the best options for your budget &amp; style</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Shop with confidence — 24/7 access, day or night</li>
                        </ul>
                        <Button onClick={openVip("landing-for-consumers")} data-testid="for-consumers-cta" className="mt-5 bg-[#9B1B30] hover:bg-[#B8233C] rounded-sm">
                            Reserve $5/mo VIP <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                    <div className="bg-white/[0.04] border border-white/10 rounded-sm p-6" data-testid="for-business-owners">
                        <div className="eyebrow text-[#D4AF37] mb-2">For business owners</div>
                        <h3 className="font-heading text-2xl mb-3">You have the products. We build the AI selling system.</h3>
                        <ul className="space-y-2 text-sm text-white/85">
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Reach more local customers who are actively looking</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Generate more sales — even when you&rsquo;re closed</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Get discovered by shoppers who don&rsquo;t know you yet</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Powered by AI. Driven by real results.</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> Founding Business Partner™ — lifetime profile, one always-active offer</li>
                        </ul>
                        <Button onClick={() => navigate("/partners")} data-testid="for-business-cta" className="mt-5 bg-[#D4AF37] text-[#0A1128] hover:bg-[#E6C65C] rounded-sm">
                            Become a Founding Business <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* JULY 4TH 250 YEARS VIDEO SECTION */}
            <section className="bg-[#050814] text-white relative grain" data-testid="july4-section">
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="text-center mb-10">
                        <div className="eyebrow text-[#D4AF37]">Featured · July 4th, 2026</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-2">
                            Celebrate 250 Years With Your Community
                        </h2>
                        <p className="mt-3 max-w-2xl mx-auto text-white/80 leading-relaxed">
                            Honoring 250 years of the United States — the families, small businesses, veterans, and neighborhoods that built this country and continue to build ours. Jacksonville, we celebrate you.
                        </p>
                    </div>

                    {/* Photo gallery — 3 verified Pexels photos on top + 3 patriotic themed
                        tiles on bottom (safer than guessing Pexels IDs that turn out to be
                        the wrong image). Uses compressed URLs (~150-250 KB each). */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="july4-gallery">
                        {/* Row 1 — real photos we've verified render Americana subjects */}
                        {[
                            { id: 1046399, alt: "American flag waving in the wind", caption: "One Nation" },
                            { id: 6942352, alt: "Fireworks lighting up the night sky", caption: "250 Years of Freedom" },
                            { id: 8422729, alt: "Jacksonville small business owner outside their shop", caption: "Built by Locals" },
                        ].map((p) => (
                            <figure
                                key={p.id}
                                data-testid={`july4-photo-${p.id}`}
                                className="relative rounded-sm overflow-hidden border border-white/10 group"
                            >
                                <img
                                    src={`https://images.pexels.com/photos/${p.id}/pexels-photo-${p.id}.jpeg?auto=compress&cs=tinysrgb&w=800&fit=crop`}
                                    srcSet={`https://images.pexels.com/photos/${p.id}/pexels-photo-${p.id}.jpeg?auto=compress&cs=tinysrgb&w=400&fit=crop 400w, https://images.pexels.com/photos/${p.id}/pexels-photo-${p.id}.jpeg?auto=compress&cs=tinysrgb&w=800&fit=crop 800w`}
                                    sizes="(max-width: 768px) 50vw, 33vw"
                                    alt={p.alt}
                                    width="800"
                                    height="600"
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-40 md:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 py-2 text-xs font-heading uppercase tracking-widest text-[#D4AF37]">
                                    {p.caption}
                                </figcaption>
                            </figure>
                        ))}
                        {/* Row 2 — themed patriotic tiles (deterministic, never break, on-brand) */}
                        {[
                            { grad: "from-[#9B1B30] via-[#7A1524] to-[#0A1128]", icon: <Star className="w-10 h-10 text-[#D4AF37]" />, big: "1776 – 2026", small: "250 Years Strong" },
                            { grad: "from-[#0A1128] via-[#1E2A6E] to-[#0A1128]", icon: <ShieldCheck className="w-10 h-10 text-[#D4AF37]" />, big: "Made in Jacksonville", small: "Built by our neighbors" },
                            { grad: "from-[#D4AF37]/20 via-[#0A1128] to-[#9B1B30]", icon: <Sparkles className="w-10 h-10 text-[#D4AF37]" />, big: "Support Local", small: "Every purchase counts" },
                        ].map((tile, i) => (
                            <div
                                key={`themed-${i}`}
                                data-testid={`july4-themed-${i}`}
                                className={`relative rounded-sm overflow-hidden border border-white/10 h-40 md:h-56 bg-gradient-to-br ${tile.grad} flex flex-col items-center justify-center text-center p-6`}
                            >
                                <div className="mb-3">{tile.icon}</div>
                                <div className="font-heading text-xl md:text-2xl text-white leading-tight">{tile.big}</div>
                                <div className="mt-1 text-xs uppercase tracking-widest text-[#D4AF37]">{tile.small}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="bg-[#FDFBF7]" data-testid="benefits-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-4">
                            <div className="eyebrow text-[#9B1B30]">Membership Benefits</div>
                            <h2 className="font-heading text-3xl sm:text-4xl mt-2">{t("sec.benefits.title")}</h2>
                            <div className="gold-rule mt-6" />
                            <p className="font-accent text-lg mt-6 text-[#1A1D24]/80">
                                One activation. One lifetime rate. Real benefits delivered locally — and a celebration of 250 years of community, story, and Main Street.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
                            {[
                                ["$100 Hotel Voucher", "Issued after activation. Redeem with approved partners.", Gift],
                                ["Local Savings", "Hand‑picked offers from vetted businesses in your city.", MapPin],
                                ["VIP Rewards", "Member‑only access, early drops, and partner incentives.", Star],
                                ["Birthday Offers (soon)", "Curated gifts and discounts on your birthday month.", Sparkles],
                                ["AI Shopping Assistant", "Ask in plain language — find, compare, save, buy from trusted local businesses.", Sparkles],
                                ["Celebration Photo Gallery", "Honor 250 Years of the United States with our community.", Play],
                            ].map(([title, desc, Icon]) => (
                                <div key={title} className="bg-white border border-black/5 rounded-sm p-6 hover:-translate-y-1 transition-all duration-300 shadow-sm">
                                    <Icon className="w-6 h-6 text-[#9B1B30]" />
                                    <div className="font-heading text-lg mt-3">{title}</div>
                                    <p className="text-sm text-[#1A1D24]/70 mt-1 leading-relaxed">{desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="bg-[#0A1128] text-white relative grain" data-testid="how-it-works">
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="eyebrow text-[#D4AF37]">{t("sec.how.eyebrow")}</div>
                    <h2 className="font-heading text-3xl sm:text-4xl mt-2 mb-10">{t("sec.how.title")}</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            ["01", "Join", "Create your account in under 2 minutes. Provide your details and accept terms."],
                            ["02", "Lock In", "Lock in your Founding VIP rate NOW during the 250-year pre-launch — just $5/month for life. When the clock runs out, the rate returns to $9.99/mo for life."],
                            ["03", "Save & Celebrate", "Get your hotel voucher, redeem local offers, and celebrate 250 Years of the United States with your community."],
                        ].map(([num, title, desc]) => (
                            <div key={num} className="bg-[#0F162A] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/40 transition-colors">
                                <div className="font-heading text-5xl text-[#D4AF37]">{num}</div>
                                <div className="font-heading text-xl mt-2">{title}</div>
                                <p className="text-sm text-white/70 mt-2 leading-relaxed">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LOCAL OFFERS PREVIEW */}
            <section className="bg-[#FDFBF7]" data-testid="offers-preview">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <div className="eyebrow text-[#9B1B30]">{t("sec.offers.eyebrow")}</div>
                            <h2 className="font-heading text-3xl sm:text-4xl mt-2">{t("sec.offers.title")}</h2>
                        </div>
                        <Link to="/offers" className="text-[#9B1B30] hover:text-[#B8233C] inline-flex items-center gap-1 font-semibold" data-testid="see-all-offers">
                            See all offers <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {offers.map((o) => (
                            <div key={o.id} className="bg-white border border-black/5 rounded-sm p-5 hover:-translate-y-1 transition-all duration-300 shadow-sm" data-testid={`offer-card-${o.id}`}>
                                <div className="flex items-start justify-between">
                                    <div className="eyebrow text-[#9B1B30]">{o.category}</div>
                                    {o.july4_celebration && <span className="text-[10px] uppercase tracking-widest bg-[#0A1128] text-[#D4AF37] px-2 py-1 rounded-sm">250 Years</span>}
                                </div>
                                <h3 className="font-heading text-lg mt-2">{o.title}</h3>
                                <div className="text-sm text-[#1A1D24]/70 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{o.business_name} · {o.city}, {o.state}</div>
                                <p className="text-xs text-[#1A1D24]/60 mt-3 leading-relaxed line-clamp-3">{o.terms}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BUSINESS PARTNER — developer/marketplace preview removed for launch */}
            <section className="bg-[#FDFBF7]" data-testid="partner-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-6">
                    <div className="text-center mb-10">
                        <div className="eyebrow text-[#9B1B30]">Founding Business Partner™</div>
                        <h2 className="font-heading text-3xl sm:text-4xl mt-2">{t("sec.partners.title")}</h2>
                        <p className="mt-3 max-w-2xl mx-auto text-[#1A1D24]/75">
                            Only the first <span className="font-semibold">100 businesses</span> receive Founding Business Partner™ status. Maintain your ADN membership and keep your business profile and one active offer on ADN <span className="text-[#9B1B30] font-semibold">for life</span>.
                        </p>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
                    <div className="relative overflow-hidden rounded-sm bg-[#0A1128] text-white p-8 lg:p-10 grain max-w-2xl mx-auto">
                        <img
                            src={BUSINESS_IMG}
                            alt="Local Jacksonville business owner"
                            width="1600"
                            height="900"
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover opacity-25"
                        />
                        <div className="relative">
                            <Building2 className="w-7 h-7 text-[#D4AF37]" />
                            <h3 className="font-heading text-2xl sm:text-3xl mt-3">{t("sec.partners.biz")}</h3>
                            <p className="mt-3 text-white/80 max-w-md">List your business, publish member offers, sponsor July 4th 250 Years content, and reach loyal local members.</p>
                            <Button onClick={() => navigate("/partners")} className="mt-5 bg-[#9B1B30] text-white hover:bg-[#B8233C] border-b-2 border-[#D4AF37] rounded-sm" data-testid="partner-business-btn">
                                {t("sec.partners.biz.cta")}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOTEL VOUCHER PROMO */}
            <section className="bg-[#0A1128] text-white relative grain" data-testid="voucher-section">
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="eyebrow text-[#D4AF37]">{t("sec.voucher.eyebrow")}</div>
                        <h2 className="font-heading text-3xl sm:text-4xl mt-2">{t("sec.voucher.title")}</h2>
                        <p className="mt-4 text-white/80 max-w-md leading-relaxed">
                            {t("sec.voucher.body")}
                        </p>
                        <Link to="/voucher-terms" className="inline-flex items-center gap-1 mt-4 text-[#D4AF37] font-semibold" data-testid="voucher-terms-link">View voucher terms <ArrowRight className="w-4 h-4" /></Link>
                    </div>
                    <div className="relative rounded-sm overflow-hidden border border-[#D4AF37]/40">
                        <img
                            src={HOTEL_IMG}
                            alt="ADN member hotel voucher destination"
                            width="1600"
                            height="900"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-72 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/95 to-transparent flex items-end p-6">
                            <div>
                                <div className="eyebrow text-[#D4AF37]">{t("sec.voucher.badge.eyebrow")}</div>
                                <div className="font-heading text-3xl">{t("sec.voucher.badge.value")}</div>
                                <div className="text-white/70 text-sm">Issued upon successful activation.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[#FDFBF7]" data-testid="faq-section">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="eyebrow text-[#9B1B30]">{t("faq.eyebrow")}</div>
                    <h2 className="font-heading text-3xl sm:text-4xl mt-2 mb-8">{t("faq.title")}</h2>
                    <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                        {[
                            ...Array.from({ length: 20 }, (_, idx) => [
                                t(`faq.q${idx + 1}`),
                                t(`faq.a${idx + 1}`),
                            ]),
                            ...customFaq.map((f) => [f.question, f.answer]),
                        ].map(([q, a], i) => (
                            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
                                <AccordionTrigger className="text-left font-heading text-lg">{q}</AccordionTrigger>
                                <AccordionContent className="text-[#1A1D24]/80 leading-relaxed">{a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-[#9B1B30] text-white" data-testid="final-cta">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <h2 className="font-heading text-3xl sm:text-4xl">{t("cta.title")}</h2>
                        <p className="mt-2 text-white/80">{t("hero.subhead.price")} · {t("hero.badge.rate")}</p>
                    </div>
                    <Button onClick={openVip("landing-final-cta")} className="h-12 px-6 bg-[#D4AF37] text-[#0A1128] hover:bg-[#E6C65C] rounded-sm text-base font-semibold" data-testid="final-cta-btn">
                        Reserve $5/mo for Life <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </section>

            {/* Floating "what's near me right now" widget — pops 3s after landing */}
            <Suspense fallback={null}><NearMeFloatingWidget /></Suspense>
            <PrelaunchVipModal
                open={vipModal.open}
                onClose={() => setVipModal({ open: false, source: "" })}
                source={vipModal.source}
            />
        </div>
    );
}
